<template>
  <div
    class="shop-product-images"
    :class="{ 'shop-product-images--autoplay': autoplay }"
    :style="stats"
  >
    <div class="shop-product-images__header" v-if="categories.length && value.length > 1">
      <InputComponent v-if="useSelect" class="shop-product-images__categories">
        <select v-model="category" class="shop-product-images__categories-dropdown">
          <option :value="null">{{ $t('all', { x: '' }) }}</option>
          <option v-for="item in categories" :key="item" :value="item">
            {{ item }}
          </option>
        </select>
      </InputComponent>
      <div v-else class="shop-product-images__categories">
        <button
          type="button"
          :class="[
            'shop-product-images__category',
            { 'shop-product-images__category--active': !category },
          ]"
          @click="category = null"
        >
          {{ $t('all', { x: '' }) }}
        </button>
        <button
          v-for="item in categories"
          :key="item"
          type="button"
          :class="[
            'shop-product-images__category',
            { 'shop-product-images__category--active': category === item },
          ]"
          @click="category = item"
        >
          {{ item }}
        </button>
      </div>
    </div>
    <Carousel
      v-model="current"
      ref="carousel"
      :class="[
        'shop-product-images__slider',
        { 'shop-product-images__slider--hidden': photoswipeActive },
      ]"
      :per-page="1"
      :pagination-enabled="false"
      :navigation-next-label="Icons.next"
      :navigation-prev-label="Icons.prev"
      :navigation-enabled="true"
      :autoplay-timeout="6000"
      loop
      :autoplay="autoplay"
      :id="'carousel_' + id"
    >
      <template #default>
        <Slide
          v-for="image in images"
          :key="image.url"
          :data-width="image.dimensions[0]"
          :data-height="image.dimensions[1]"
          :data-caption="getImageCaption(image)"
          :data-src="image.url"
          class="shop-product-images__image"
        >
          <img :src="image.url" :alt="image.description" ref="items" />
          <div class="shop-product-images__image-description">
            <span v-if="image.description">{{ image.description }}</span>
          </div>
        </Slide>
        <Slide
          v-if="!images.length"
          class="shop-product-images__image shop-product-images__image-placeholder"
        >
          <Icon icon="fa-pro-light:box-alt" size="4x" />
        </Slide>
      </template>
    </Carousel>

    <div class="shop-product-images__footer">{{ footer }}</div>
  </div>
</template>

<script>
import { Carousel, Slide } from '../components/VueCarousel'
import { InputComponent } from 'vue-elder-input'
import Icon from '@kvass/vue2-icon'

/** PhotoSwipe */

import PhotoSwipeLightbox from 'photoswipe/dist/photoswipe-lightbox.esm.js'
import 'photoswipe/dist/photoswipe.css'
import PhotoSwipeDynamicCaption from 'photoswipe-dynamic-caption-plugin'
import 'photoswipe-dynamic-caption-plugin/photoswipe-dynamic-caption-plugin.css'

const Icons = {
  next: `<svg viewBox="0 0 320 512"><path d="M278.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-160 160c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L210.7 256 73.4 118.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l160 160z"/></svg>`,
  prev: `<svg viewBox="0 0 320 512"><path d="M41.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 256 246.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z"/></svg>`,
}

export default {
  props: {
    value: Array,
    title: String,
    startIndex: Number,
  },
  watch: {
    value: {
      handler: 'init',
      immediate: true,
    },
    images: {
      handler: 'initPhotoswipe',
      immediate: true,
    },
    category: {
      handler(val) {
        if (val) this.current = 0
      },
    },
    current: {
      handler(val) {
        this.$emit('index', val)
      },
    },
  },
  data() {
    return {
      current: 0,
      category: null,
      useSelect: false,
      photoswipe: null,
      photoswipeCaption: null,
      photoswipeActive: false,
      Icons,
    }
  },
  computed: {
    autoplay() {
      return this.$attrs.autoplay !== false && !this.photoswipeActive
    },
    id() {
      return this._uid
    },
    images() {
      return this.value.filter(({ tags = [] }) => !this.category || tags.includes(this.category))
    },
    categories() {
      return Array.from(new Set(this.value.map(({ tags = [] }) => tags).flat()))
    },
    stats() {
      const defaultRatio = 16 / 9
      const RoundTo = (v, d = 5) => v
      const getRatio = item => {
        if (!item) return
        return item.dimensions.at(0) / item.dimensions.at(1)
      }
      const ratios = this.value
        .filter(({ dimensions: d }) => d.length)
        .map(getRatio)
        .sort()

      const current = this.images.at(this.current) || { dimensions: [] }

      return {
        '--min-ratio': RoundTo(ratios.at(-1) || defaultRatio),
        '--max-ratio': RoundTo(ratios.at(0) || defaultRatio),
        '--current-ratio': RoundTo(getRatio(current) || defaultRatio),
        '--current-height': current.dimensions.at(1) + 'px',
        '--current-width': current.dimensions.at(0) + 'px',
      }
    },
    footer() {
      if (this.images.length < 2) return this.$t('imagesOnlyAsIllustration')
      return `${this.current + 1} ${this.$t('of')} ${this.images.length} — ${this.$t(
        'imagesOnlyAsIllustration',
      )}`
    },
  },
  methods: {
    getImageCaption(image) {
      if (!image.description) return
      if (this.title) return `<h3>${this.title}</h3>${image.description}`
      return image.description
    },
    init() {
      this.current = Math.min(this.startIndex || 0, this.value.length)
      this.category = null
      this.initSelect()
    },
    initPhotoswipe() {
      if (this.photoswipe) this.photoswipe.destroy()
      if (!this.images.length) return

      this.$nextTick(() => {
        this.photoswipe = new PhotoSwipeLightbox({
          gallery: `#carousel_${this.id} .VueCarousel-inner`,
          children: '.shop-product-images__image',
          bgOpacity: '.9',
          closeTitle: this.$t('close'),
          zoomTitle: this.$t('zoomImage'),
          arrowPrevTitle: this.$t('prev'),
          arrowNextTitle: this.$t('next'),
          errorMsg: this.$t('somethingWentWrong'),
          indexIndicatorSep: ` ${this.$t('of')} `,
          pswpModule: () => import('photoswipe'),
        })

        this.photoswipe.addFilter('domItemData', (itemData, element) => {
          if (element) {
            itemData.src = element.getAttribute('data-src')
            itemData.w = Number(element.getAttribute('data-width'))
            itemData.h = Number(element.getAttribute('data-height'))
            itemData.msrc = itemData.src
            itemData.thumbCropped = true
          }

          return itemData
        })

        this.photoswipe.addFilter('thumbBounds', (thumbBounds, itemData, index) => {
          const bounds = itemData.element.getBoundingClientRect()

          const imgRatio = itemData.w / itemData.h
          const containerRatio = bounds.width / bounds.height

          const width = imgRatio >= containerRatio ? bounds.width : bounds.height * imgRatio
          const height = imgRatio >= containerRatio ? bounds.width / imgRatio : bounds.height

          thumbBounds.innerRect.x = bounds.left + (bounds.width - width) / 2
          thumbBounds.innerRect.y = bounds.top
          thumbBounds.innerRect.w = width + thumbBounds.innerRect.x
          thumbBounds.innerRect.h = height + thumbBounds.innerRect.y

          thumbBounds.w = width
          thumbBounds.y = thumbBounds.innerRect.y
          thumbBounds.x = thumbBounds.innerRect.x

          return thumbBounds
        })

        this.photoswipe.on('openingAnimationStart', () => {
          this.photoswipeActive = true
        })
        this.photoswipe.on('closingAnimationEnd', () => {
          this.photoswipeActive = false
        })

        this.photoswipe.on('change', (...args) => {
          this.current = this.photoswipe.pswp.currSlide.index
        })

        this.photoswipeCaption = new PhotoSwipeDynamicCaption(this.photoswipe, {
          type: 'auto',
          captionContent: slide => {
            if (!slide.data.element) return
            return slide.data.element.getAttribute('data-caption')
          },
        })

        this.photoswipe.init()
      })
    },
    initSelect() {
      this.$nextTick(() => {
        const container = this.$el
        if (!container || !container.querySelector) return

        const categories = this.$el.querySelector('.shop-product-images__categories')
        if (!categories) return

        this.useSelect = categories.clientHeight > categories.children[0].clientHeight
      })
    },
  },
  beforeDestroy() {
    if (this.photoswipe) this.photoswipe.destroy()
  },
  components: {
    Carousel,
    Slide,
    Icon,
    InputComponent,
  },
}
</script>

<style lang="scss">
@import '../mixins.scss';

.shop-product-images {
  $spacing: 1rem;

  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  align-self: stretch;

  position: relative;

  min-height: 65dvh;

  &__footer {
    font-size: 0.7em;
    opacity: 0.7;
    white-space: nowrap;
  }

  &__header {
    position: absolute;
    top: 0.5rem;
    left: 0.5rem;

    width: calc(100% - 1rem);

    display: flex;

    z-index: 1;
  }

  &__categories {
    display: flex;
    flex-wrap: wrap;
    border: 1px solid var(--border-color);
    border-radius: var(--border-radius);
    overflow: hidden;
    font-size: 0.8em;
    box-shadow: 0 5px 15px -5px rgba(black, 0.1);
    transform: translate3d(0, 0, 0); // Safari hack

    max-width: 75%;

    .elder-input__field {
      border: none;
    }

    &.elder-input {
      padding: 0.25rem 0.5rem;
      background-color: white;

      &:focus-within {
        outline: 1px solid var(--primary);
      }
    }

    &-dropdown {
      outline: none;
    }
  }

  &__category {
    background-color: css-lighten('grey', 7%);
    padding: 0.25rem 0.8rem;
    cursor: pointer;

    &:not(&--active) {
      color: css-darken('grey', 30%);
    }

    &--active {
      background-color: white;
    }

    & + & {
      border-left: 1px solid var(--border-color);
    }
  }

  &__slider {
    &--hidden {
      opacity: 0;
    }
  }

  &__image {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: flex-start;

    img {
      width: 100%;
      height: 100%;

      position: absolute;
      top: 0;
      left: 0;

      object-fit: contain;
      object-position: left top;
    }

    svg {
      opacity: 0.1;
      margin-inline: auto;
    }

    &:not(:has(img)) {
      background-color: rgba(black, 0.03);
    }

    svg {
      opacity: 0.1;
    }

    &-description {
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;

      display: flex;
      justify-content: center;

      padding-bottom: 0.5rem;

      span {
        display: block;
        margin-inline: -0.5rem;
        padding: 0.4rem 0.6rem;
        background-color: rgba(white, 0.8);
        z-index: 1114;
        border-radius: var(--border-radius);
        font-size: 0.8em;
        backdrop-filter: blur(8px);

        max-width: 100ch;
      }
    }
  }

  .VueCarousel {
    flex-grow: 1;
    // max-height: calc(var(--slide-width) / var(--current-ratio));

    &:hover {
      .VueCarousel-navigation-button {
        opacity: 1;
      }
    }
  }

  &--autoplay .VueCarousel-navigation-button {
    opacity: 0;
  }

  .VueCarousel-wrapper {
    height: 100%;
    // border: 1px solid var(--border-color);
    // border-radius: var(--border-radius);
  }

  .VueCarousel-inner {
    height: 100% !important;
  }

  .VueCarousel-navigation {
    flex-shrink: 0;
  }

  .VueCarousel-navigation-button {
    $size: 1.75rem;

    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;

    transition: opacity 100ms ease-out;

    width: $size;
    height: $size;

    margin-inline: 0.5rem !important;

    background-color: white !important;

    transform: initial !important;
    translate: 0 -50%;

    box-shadow: 0 5px 15px -5px rgba(black, 0.6);

    &:focus {
      outline: 1px solid var(--primary);
    }

    svg {
      width: 0.6em;
      fill: rgba(black, 0.5);

      flex-shrink: 0;
    }
  }
}
</style>
